import React from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useCognitoAuthContext } from '@caplend/cognito-auth';
import hansepayLogo from '../../assets/hansepaylogo.png';
import {
  HomeIcon,
  CreditCardIcon,
  SendArrowIcon,
  UsersIcon,
  HistoryIcon,
  SidebarLogoutIcon,
  CloseIcon,
} from '../../assets/icons';
import Cognito from '../../auth/cognito';
import { oauthLogout } from '../../auth/configureAmplify';

const navigationItems = [
  { title: 'Home', url: '/dashboard', icon: HomeIcon },
  { title: 'Accounts', url: '/accounts', icon: CreditCardIcon },
  { title: 'Send Money', url: '/send-money', icon: SendArrowIcon },
  { title: 'Recipients', url: '/recipients', icon: UsersIcon },
  { title: 'Transactions', url: '/transaction-history', icon: HistoryIcon },
];

const iconClassName = 'h-[18px] w-[18px] shrink-0';
const iconStrokeWidth = 1.6;

function navLinkClassName(isActive) {
  return [
    'relative flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm no-underline transition-[background,color] duration-150',
    isActive
      ? 'bg-[var(--n50)] font-medium text-[var(--n800)]'
      : 'bg-transparent font-normal text-[var(--text-secondary)] hover:bg-[var(--n50)]/60',
  ].join(' ');
}

function SidebarBrand() {
  return (
    <div className='mb-2 px-2 pb-[18px]'>
      <NavLink to='/dashboard' className='inline-flex items-center gap-2 leading-none no-underline' aria-label='HansePay home'>
        <img src={hansepayLogo} width={40} height={40} alt='HansePay' className='block shrink-0' />
        <span className='wordmark inline-block translate-y-px text-[23px] leading-none tracking-[-0.015em]'>
          HansePay
        </span>
      </NavLink>
    </div>
  );
}

function SidebarNavLink({ item, isActive, onSendMoneyReclick }) {
  const Icon = item.icon;
  const tourAttr = item.url === '/send-money' ? { 'data-tour': 'sidebar-send-money' } : {};

  return (
    <NavLink
      to={item.url}
      className={navLinkClassName(isActive)}
      onClick={(event) => {
        if (item.url === '/send-money' && isActive) {
          event.preventDefault();
          onSendMoneyReclick();
        }
      }}
      {...tourAttr}
    >
      {isActive && <span className='absolute bottom-2 left-0 top-2 w-0.5 rounded-sm bg-[var(--n500)]' />}
      <Icon
        className={`${iconClassName} ${isActive ? 'text-[var(--n800)]' : 'text-[var(--text-tertiary)]'}`}
        strokeWidth={iconStrokeWidth}
      />
      <span>{item.title}</span>
    </NavLink>
  );
}

SidebarNavLink.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
  }).isRequired,
  isActive: PropTypes.bool.isRequired,
  onSendMoneyReclick: PropTypes.func.isRequired,
};

function SidebarNav({ isPathActive, onSendMoneyReclick }) {
  return (
    <>
      <div className='nav-label px-3 pb-2.5'>Navigation</div>
      <nav className='flex flex-col gap-px'>
        {navigationItems.map((item) => (
          <SidebarNavLink
            key={item.title}
            item={item}
            isActive={isPathActive(item.url)}
            onSendMoneyReclick={onSendMoneyReclick}
          />
        ))}
      </nav>
    </>
  );
}

SidebarNav.propTypes = {
  isPathActive: PropTypes.func.isRequired,
  onSendMoneyReclick: PropTypes.func.isRequired,
};

function SidebarLogout({ onLogout }) {
  return (
    <div className='pt-2'>
      <button type='button' onClick={onLogout} className={navLinkClassName(false)}>
        <SidebarLogoutIcon className={`${iconClassName} text-[var(--text-tertiary)]`} strokeWidth={iconStrokeWidth} />
        <span>Log out</span>
      </button>
    </div>
  );
}

SidebarLogout.propTypes = {
  onLogout: PropTypes.func.isRequired,
};

function SidebarContent({ onLogout, isPathActive, onSendMoneyReclick, showBrand = true }) {
  return (
    <>
      {showBrand && <SidebarBrand />}
      <SidebarNav isPathActive={isPathActive} onSendMoneyReclick={onSendMoneyReclick} />
      <div className='flex-1' />
      <SidebarLogout onLogout={onLogout} />
    </>
  );
}

SidebarContent.propTypes = {
  onLogout: PropTypes.func.isRequired,
  isPathActive: PropTypes.func.isRequired,
  onSendMoneyReclick: PropTypes.func.isRequired,
  showBrand: PropTypes.bool,
};

export default function Sidebar({ isOpen, onToggle, isMobile = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const { setHasAccessToken } = useCognitoAuthContext();

  const isPathActive = (path) => {
    if (path === '/dashboard') {
      return currentPath === path || currentPath === '/';
    }
    if (path === '/accounts') {
      return currentPath === path || currentPath.startsWith('/accounts/');
    }
    if (path === '/transaction-history') {
      return currentPath === path || currentPath.startsWith('/transaction-history/');
    }
    if (path === '/recipients') {
      return currentPath === path || currentPath.startsWith('/recipients/');
    }
    if (path === '/send-money') {
      return currentPath === path || currentPath.startsWith('/send-money/');
    }
    return currentPath === path;
  };

  const handleSendMoneyReclick = () => {
    navigate('/send-money', { replace: true, state: { sendMoneyReset: Date.now() } });
  };

  const handleLogout = () => {
    if (process.env.REACT_APP_COGNITO_DOMAIN) {
      oauthLogout();
      return;
    }
    setHasAccessToken(false);
    Cognito.logOut();
  };

  if (isMobile) {
    return (
      <>
        {isOpen && <button type='button' className='fixed inset-0 z-40 bg-black/50' onClick={onToggle} aria-label='Close sidebar' />}

        <div
          className={`
          fixed top-0 left-0 z-50 flex h-full w-64 flex-col border border-[var(--color-border)] bg-[var(--white)] px-[14px] py-6
          shadow-[var(--shadow-sidebar)]
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        >
          <div className='mb-2 flex items-start justify-between'>
            <SidebarBrand />
            <button
              type='button'
              onClick={onToggle}
              className='shrink-0 rounded-md p-1 text-[var(--text-secondary)] hover:bg-[var(--n50)]'
              aria-label='Close sidebar'
            >
              <CloseIcon className='h-5 w-5' />
            </button>
          </div>
          <SidebarContent
            onLogout={handleLogout}
            isPathActive={isPathActive}
            onSendMoneyReclick={handleSendMoneyReclick}
            showBrand={false}
          />
        </div>
      </>
    );
  }

  return (
    <div
      className={`
      fixed top-5 left-5 z-20 flex h-[calc(100vh-40px)] w-[212px] flex-col
      rounded-[18px] border border-[var(--color-border)] bg-[var(--white)] px-[14px] py-6
      shadow-[var(--shadow-sidebar)]
      transition-all duration-300 ease-in-out
      ${isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}
    `}
    >
      <SidebarContent onLogout={handleLogout} isPathActive={isPathActive} onSendMoneyReclick={handleSendMoneyReclick} />
    </div>
  );
}

Sidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
};
